import React, { useRef, useState, useEffect, useMemo } from "react";
import { Annotation, Point } from "../types/annotation.types";
import allImgPaths from "assets/images";

interface Props {
  imageSrc: string;
  // annotations: Annotation[];
  annotations: any[];
  onAddPoint: (point: Point) => void;
  tempPoints: { start?: Point; end?: Point };
  imageRef: any;
  canvasRef: any;
  imageScale?: number;
  fontInfo?: any;
}

const AnnotationCanvas2: React.FC<Props> = ({
  imageSrc,
  annotations,
  onAddPoint,
  tempPoints,
  imageRef,
  canvasRef,
  imageScale = 1, // Default scale is 1 (100%)
  fontInfo
}) => {
  const [imgDims, setImgDims] = useState({ width: 0, height: 0 });

  const [fontSize, setFontSize] = useState<any>(fontInfo?.size || 24)
  const [fontName, setFontName] = useState<any>(fontInfo?.name?.replace(/\.ttf$/, "") || 'Arial')

  console.log({ annotations })

  const handleClick = (e: any) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Normalize coordinates to 0-1000 scale (like in ImageVisualizer)
    const xNorm = Math.round((x / rect.width) * 1000);
    const yNorm = Math.round((y / rect.height) * 1000);

    onAddPoint({ x: xNorm, y: yNorm });
  };

  // Calculate dynamic font size based on image dimensions and scale factor
  const calculateDynamicFontSize = useMemo(() => {
    if (!imageRef.current || !imgDims.width || !imgDims.height) {
      return 22;
    }

    const avgDimension = (imgDims.width + imgDims.height) / 2;
    const baseFontSize = Math.min(
      32,
      Math.max(10, Math.round(avgDimension * 0.03))
    );

    let finalFontSize;
    if (imageScale <= 1.0) {
      finalFontSize = Math.max(10, Math.round(baseFontSize * imageScale * 1.0));
    } else {
      finalFontSize = Math.max(
        10,
        Math.round(baseFontSize / (imageScale * 1.0))
      );
    }

    return finalFontSize;
  }, [imgDims.width, imgDims.height, imageScale]);

  // Calculate dynamic arrow properties based on font size and image scale
  const calculateArrowProperties = useMemo(() => {
    const baseLineWidth = 2;
    const baseHeadLength = 10;

    let lineWidth, headLength;

    if (imageScale <= 1.0) {
      lineWidth = Math.max(1, Math.round(baseLineWidth * imageScale));
      headLength = Math.max(5, Math.round(baseHeadLength * imageScale));
    } else {
      lineWidth = Math.max(1, Math.round(baseLineWidth / imageScale));
      headLength = Math.max(5, Math.round(baseHeadLength / imageScale));
    }

    return { lineWidth, headLength };
  }, [imageScale]);

  const drawArrow = (
    ctx: CanvasRenderingContext2D,
    start: Point, // arrow start
    end: Point,   // arrow end (tip)
    text: string,
    textStart?: Point,
    textEnd?: Point,
    fontSize: number = 16,
    lineHeight: number = 20
  ) => {
    ctx.strokeStyle = "red";
    ctx.fillStyle = "red";
    ctx.lineWidth = 2;
    ctx.font = `${fontSize}px ${fontName}`;
    ctx.textBaseline = "top";

    // 1. Draw arrow line
    ctx.beginPath();
    ctx.moveTo(start.x, start.y);
    ctx.lineTo(end.x, end.y);
    ctx.stroke();

    // 2. Draw arrowhead
    const angle = Math.atan2(end.y - start.y, end.x - start.x);
    const headLength = 10;
    ctx.beginPath();
    ctx.moveTo(end.x, end.y);
    ctx.lineTo(
      end.x - headLength * Math.cos(angle - Math.PI / 6),
      end.y - headLength * Math.sin(angle - Math.PI / 6)
    );
    ctx.lineTo(
      end.x - headLength * Math.cos(angle + Math.PI / 6),
      end.y - headLength * Math.sin(angle + Math.PI / 6)
    );
    ctx.lineTo(end.x, end.y);
    ctx.fill();

    // 3. Use text box position and width
    if (!textStart || !textEnd) return; // skip if no text box

    const padding = 2;
    const x = textStart.x + padding;
    const y = textStart.y + padding;
    const maxWidth = Math.abs(textEnd.x - textStart.x) - 2 * padding;

    // 4. Wrap and draw text
    const words = text.split(" ");
    let line = "";
    const lines: string[] = [];

    for (let i = 0; i < words.length; i++) {
      const testLine = line + words[i] + " ";
      if (ctx.measureText(testLine).width > maxWidth && i > 0) {
        lines.push(line.trim());
        line = words[i] + " ";
      } else {
        line = testLine;
      }
    }
    lines.push(line.trim());

    // Draw each line
    lines.forEach((l, idx) => {
      ctx.fillText(l, x, y + idx * lineHeight);
    });
  };

  const draw = (ctx: CanvasRenderingContext2D) => {
    ctx.clearRect(0, 0, imgDims.width, imgDims.height);
    ctx.lineWidth = 2;
    ctx.strokeStyle = "red";
    ctx.fillStyle = "red";

    // Draw the image first
    if (imageRef.current) {
      ctx.drawImage(imageRef.current, 0, 0, imgDims.width, imgDims.height);
    }

    const arrowAnnotations = annotations.filter(item => item.arrow_start_coordinate && item.arrow_end_coordinate);
    const directAnnotations = annotations.filter(item => item.start && item.end);

    console.log({ arrowAnnotations, imgDims });
    const xScale = imgDims.width / 1000;
    const yScale = imgDims.height / 1000;

    arrowAnnotations.forEach((ann) => {
      const start = {
        x: ann?.isManual ? (ann.arrow_start_coordinate[0] * xScale) : ann.arrow_start_coordinate[0],
        y: ann?.isManual ? (ann.arrow_start_coordinate[1] * yScale) : ann.arrow_start_coordinate[1],
      };
      const end = {
        x: ann?.isManual ? (ann.arrow_end_coordinate[0] * xScale) : ann.arrow_end_coordinate[0],
        y: ann?.isManual ? (ann.arrow_end_coordinate[1] * yScale) : ann.arrow_end_coordinate[1],
      };
      const textStart = ann.text_start_coordinate
        ? {
          x: ann?.isManual ? (ann.text_start_coordinate[0] * xScale) : ann.text_start_coordinate[0],
          y: ann?.isManual ? (ann.text_start_coordinate[1] * yScale) : ann.text_start_coordinate[1],
        }
        : undefined;

      const textEnd = ann.text_end_coordinate
        ? {
          x: ann?.isManual ? (ann.text_end_coordinate[0] * xScale) : ann.text_end_coordinate[0],
          y: ann?.isManual ? (ann.text_end_coordinate[1] * yScale) : ann.text_end_coordinate[1],
        }
        : undefined;

      drawArrow(ctx, start, end, ann.label, textStart, textEnd);
    });

    directAnnotations.forEach(({ start, end, text }) => {
      // Convert normalized coordinates (0-1000) to canvas coordinates
      const xScale = imgDims.width / 1000;
      const yScale = imgDims.height / 1000;
      const scaledStart = { x: start.x * xScale, y: start.y * yScale };
      const scaledEnd = { x: end.x * xScale, y: end.y * yScale };
      drawArrowOriginal(ctx, scaledStart, scaledEnd, text);
    });

    console.log({ tempPoints })

    // Draw temporary points/arrow
    if (tempPoints.start && !tempPoints.end) {
      // Convert normalized coordinates to canvas coordinates
      const xScale = imgDims.width / 1000;
      const yScale = imgDims.height / 1000;
      const scaledX = tempPoints.start.x * xScale;
      const scaledY = tempPoints.start.y * yScale;

      ctx.beginPath();
      ctx.arc(scaledX, scaledY, 5, 0, 2 * Math.PI);
      ctx.fill();
    } else if (tempPoints.start && tempPoints.end) {
      // Convert normalized coordinates to canvas coordinates
      const xScale = imgDims.width / 1000;
      const yScale = imgDims.height / 1000;

      const end = {
        x: tempPoints.start.x * xScale,
        y: tempPoints.start.y * yScale,
      };
      const start = {
        x: tempPoints.end.x * xScale,
        y: tempPoints.end.y * yScale,
      };
      drawArrow(ctx, start, end, "");
    }
  };

  const drawArrowOriginal = (
    ctx: CanvasRenderingContext2D,
    start: Point,
    end: Point,
    text: string
  ) => {
    const { headLength, lineWidth } = calculateArrowProperties;

    const dx = start.x - end.x;
    const dy = start.y - end.y;
    const angle = Math.atan2(dy, dx);

    ctx.beginPath();
    ctx.lineWidth = lineWidth;
    const extension = 50;
    const extendedEndX = end.x - extension * Math.cos(angle);
    const extendedEndY = end.y - extension * Math.sin(angle);

    // ctx.moveTo(end.x, end.y);
    ctx.moveTo(extendedEndX, extendedEndY);
    ctx.lineTo(start.x, start.y);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(start.x, start.y);
    ctx.lineTo(
      start.x - headLength * Math.cos(angle - Math.PI / 6),
      start.y - headLength * Math.sin(angle - Math.PI / 6)
    );
    ctx.lineTo(
      start.x - headLength * Math.cos(angle + Math.PI / 6),
      start.y - headLength * Math.sin(angle + Math.PI / 6)
    );
    ctx.lineTo(start.x, start.y);
    ctx.fill();

    if (!text) return;

    ctx.font = `${calculateDynamicFontSize}px Arial`;
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillStyle = "red";

    const arrowLength = Math.sqrt(
      Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2)
    );
    const textOffset = Math.max(80, arrowLength * 0.5);
    const reverseAngle = Math.atan2(end.y - start.y, end.x - start.x);
    let textX = end.x + textOffset * Math.cos(reverseAngle);
    let textY = end.y + textOffset * Math.sin(reverseAngle);

    const maxWidth = 250;
    const lineHeight = 24;
    const padding = 10;

    const words = text.split(" ");
    let line = "";
    const lines: string[] = [];

    for (let i = 0; i < words.length; i++) {
      const testLine = line + words[i] + " ";
      const metrics = ctx.measureText(testLine);
      if (metrics.width > maxWidth && i > 0) {
        lines.push(line);
        line = words[i] + " ";
      } else {
        line = testLine;
      }
    }
    lines.push(line);

    const textWidth = lines.reduce((max, l) => {
      return Math.max(max, ctx.measureText(l).width);
    }, 0);
    const textHeight = lines.length * lineHeight;

    // 🛑 Clamp textX and textY to ensure the box stays within canvas bounds
    const canvasWidth = ctx.canvas.width;
    const canvasHeight = ctx.canvas.height;

    if (textX + textWidth + padding > canvasWidth) {
      textX = canvasWidth - textWidth - padding;
    }
    if (textY + textHeight + padding > canvasHeight) {
      textY = canvasHeight - textHeight - padding;
    }

    if (textX < padding) textX = padding;
    if (textY < padding) textY = padding;

    lines.forEach((line, index) => {
      ctx.fillText(line, textX, textY + index * lineHeight);
    });
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas && imgDims.width > 0) {
      const ctx = canvas.getContext("2d");
      if (ctx) draw(ctx);
    }
  }, [annotations, tempPoints, imgDims, imageRef, imageScale]);

  return (
    <div className="relative w-full max-w-full mx-auto min-h-[55rem]">
      <img
        ref={imageRef}
        src={imageSrc}
        crossOrigin="anonymous"
        onClick={handleClick}
        onLoad={(e) => {
          const img = e.currentTarget;
          setImgDims({
            width: img.naturalWidth,
            height: img.naturalHeight,
          });
        }}
        style={{
          display: "block",
          maxWidth: "100%",
          transform: `scale(${imageScale})`,
          transformOrigin: "top left",
        }}
        alt="Annotatable"
      />
      <canvas
        ref={canvasRef}
        width={imgDims.width}
        height={imgDims.height}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          pointerEvents: "none",
          transform: `scale(${imageScale})`,
          transformOrigin: "top left",
        }}
      />
    </div>
  );
};

export default AnnotationCanvas2;
